/*
 * Barra de progreso por tramos del carrito.
 *
 * El estado correcto lo pinta Liquid en cada render de la seccion 'cart-drawer'
 * (Dawn reemplaza el innerHTML de .drawer__inner en cada cambio de cantidad).
 * Esta clase existe para actualizar el DOM al instante, en cuanto cart.js
 * publica 'cart-update', sin esperar a que llegue el HTML del servidor.
 *
 * Los tramos llegan en data-tiers desde snippets/cart-drawer-progress.liquid:
 * ahi esta la unica definicion de umbrales y mensajes. El reparto por segmentos
 * se replica aqui con la misma formula que usa Liquid.
 */
class CartProgress extends HTMLElement {
  connectedCallback() {
    this.label = this.querySelector('.cart-progress__label');
    this.track = this.querySelector('.cart-progress__segments');
    this.fills = Array.from(this.querySelectorAll('.cart-progress__fill'));
    this.milestones = Array.from(this.querySelectorAll('.cart-progress__milestone'));

    try {
      this.config = JSON.parse(this.dataset.tiers);
    } catch (error) {
      this.config = null;
    }

    // pubsub.js y constants.js se cargan con defer en el head, asi que ya estan
    // disponibles aqui. Se comprueba de todas formas: sin ellos la barra sigue
    // funcionando via re-render de la seccion.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const itemCount = event && event.cartData ? event.cartData.item_count : undefined;
        if (typeof itemCount === 'number') this.update(itemCount);
      });
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  // Mismo calculo que hace Liquid: el nivel es el numero de umbrales superados.
  levelFor(itemCount) {
    return this.config.thresholds.reduce((level, threshold, index) => {
      return itemCount >= threshold ? index + 1 : level;
    }, 0);
  }

  // Relleno de cada segmento: proporcion recorrida entre el umbral anterior y
  // el propio, acotada a [0, 100].
  fillsFor(itemCount) {
    let previous = 0;

    return this.config.thresholds.map((threshold) => {
      const span = threshold - previous;
      const progressed = itemCount - previous;
      previous = threshold;

      if (progressed >= span) return 100;
      if (progressed <= 0 || span <= 0) return 0;
      return Math.round((progressed * 100) / span);
    });
  }

  // Con el carrito vacio la frase no sigue el patron "te faltan [n]", asi que
  // Liquid manda un texto aparte en emptyMessage.
  messageFor(level, itemCount) {
    if (itemCount === 0 && this.config.emptyMessage) return this.config.emptyMessage;

    const message = this.config.messages[level];
    if (level >= this.config.thresholds.length) return message;

    const remaining = this.config.thresholds[level] - itemCount;
    return message.replace('[n]', remaining).replace('[s]', remaining === 1 ? '' : 's');
  }

  update(itemCount) {
    if (!this.config || !this.config.thresholds) return;

    const count = Math.max(itemCount, 0);
    const level = this.levelFor(count);
    const fills = this.fillsFor(count);
    const message = this.messageFor(level, count);

    this.dataset.itemCount = count;

    this.fills.forEach((fill, index) => {
      fill.style.width = `${fills[index]}%`;
    });

    if (this.label) this.label.textContent = message;

    if (this.track) {
      // Math.floor y no round: Liquid usa division entera y los dos valores
      // tienen que coincidir para que aria-valuenow no baile en cada render.
      const overall = Math.floor(fills.reduce((sum, fill) => sum + fill, 0) / fills.length);
      this.track.setAttribute('aria-valuenow', overall);
      this.track.setAttribute('aria-label', message);
    }

    this.milestones.forEach((milestone, index) => {
      milestone.classList.toggle('is-active', level >= index + 1);
    });
  }
}

if (!customElements.get('cart-progress')) {
  customElements.define('cart-progress', CartProgress);
}
